import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  App,
  Popconfirm,
  Space,
  Table,
  Typography,
} from "antd";
import { ReloadOutlined, DeleteOutlined } from "@ant-design/icons";
import { Button, Card, Badge, PageHeader } from "../../components/Common";
import { useMutations } from "../../hooks/useApiMutation";
import LogDetailsModal from "./components/LogsDetailsModal";
import LogsFilters from "./components/LogsFilters";

const { Text } = Typography;

const formatDate = (value) => {
  if (!value) return "-";
  try {
    return new Date(value).toLocaleString();
  } catch (error) {
    return value;
  }
};

const statusVariant = (code) => {
  if (code >= 200 && code < 300) return "success";
  if (code >= 400 && code < 500) return "warning";
  if (code >= 500) return "error";
  return "neutral";
};

const LogsPage = () => {
  const { message } = App.useApp?.() || {
    message: { success: () => {}, info: () => {} },
  };
  const { mutate, loading } = useMutations();

  const [search, setSearch] = useState("");
  const [range, setRange] = useState(null);
  const [appliedFilters, setAppliedFilters] = useState({
    search: "",
    startDate: null,
    endDate: null,
  });
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });
  const [logs, setLogs] = useState([]);
  const [selectedLog, setSelectedLog] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const loadLogs = useCallback(() => {
    const params = {
      page: pagination.current,
      pageSize: pagination.pageSize,
    };

    if (appliedFilters.startDate) params.startDate = appliedFilters.startDate;
    if (appliedFilters.endDate) params.endDate = appliedFilters.endDate;
    if (appliedFilters.search) params.search = appliedFilters.search;

    mutate("fetchLogs", {
      method: "get",
      endpoint: "/logs",
      params,
      onSuccess: (data) => {
        const items = Array.isArray(data?.items) ? data.items : [];
        setLogs(items);
        setPagination((prev) => {
          const next = {
            current: data?.page ?? prev.current,
            pageSize: data?.pageSize ?? prev.pageSize,
            total: data?.totalCount ?? prev.total,
          };

          if (
            next.current === prev.current &&
            next.pageSize === prev.pageSize &&
            next.total === prev.total
          ) {
            return prev;
          }

          return next;
        });
      },
    });
  }, [
    mutate,
    pagination.current,
    pagination.pageSize,
    appliedFilters.startDate,
    appliedFilters.endDate,
    appliedFilters.search,
  ]);

  useEffect(() => {
    loadLogs();
  }, []);

  const handleApplyFilters = useCallback(() => {
    setAppliedFilters({
      search,
      startDate: range?.[0]?.toISOString() ?? null,
      endDate: range?.[1]?.toISOString() ?? null,
    });
    setPagination((prev) => ({ ...prev, current: 1 }));
  }, [search, range]);

  const handleResetFilters = useCallback(() => {
    setSearch("");
    setRange(null);
    setAppliedFilters({ search: "", startDate: null, endDate: null });
    setPagination((prev) => ({ ...prev, current: 1 }));
  }, []);

  const handlePageSizeChange = useCallback((pageSize) => {
    setPagination((prev) => ({ ...prev, pageSize, current: 1 }));
  }, []);

  const handleTableChange = useCallback((nextPagination) => {
    setPagination((prev) => ({
      ...prev,
      current: nextPagination.current ?? prev.current,
      pageSize: nextPagination.pageSize ?? prev.pageSize,
    }));
  }, []);

  const handleDelete = useCallback(
    async (id) => {
      setDeletingId(id);
      try {
        await mutate("deleteLog", {
          method: "delete",
          endpoint: `/logs/${id}`,
          onSuccess: (result) => {
            if (result) {
              message?.success?.("Log deleted successfully");
              loadLogs();
            } else {
              message?.info?.("Log was already removed");
              loadLogs();
            }
          },
        });
      } finally {
        setDeletingId(null);
      }
    },
    [message]
  );

  const handleClearAll = useCallback(async () => {
    await mutate("clearLogs", {
      method: "delete",
      endpoint: "/logs",
      onSuccess: (result) => {
        if (result) {
          message?.success?.("All logs cleared");
        } else {
          message?.info?.("Logs were already empty");
        }

        setSelectedLog(null);
        setLogs([]);
        setPagination((prev) => ({ ...prev, current: 1, total: 0 }));
      },
    });
  }, [message]);

  const handleRefresh = useCallback(() => {
    loadLogs();
  }, []);

  const columns = useMemo(
    () => [
      {
        title: "Timestamp",
        dataIndex: "createdAt",
        key: "createdAt",
        width: 200,
        render: (value) => formatDate(value),
      },
      {
        title: "API key",
        dataIndex: "apiKey",
        key: "apiKey",
        render: (value) => (value ? <Text code>{value}</Text> : "-"),
      },
      {
        title: "Endpoint",
        dataIndex: "endpoint",
        key: "endpoint",
        ellipsis: true,
      },
      {
        title: "Status",
        dataIndex: "statusCode",
        key: "statusCode",
        width: 120,
        render: (value) => (
          <Badge variant={statusVariant(value)}>{value}</Badge>
        ),
      },
      {
        title: "Billing",
        dataIndex: "billingStatus",
        key: "billingStatus",
        width: 170,
        // T-2707: why a successful call was (not) billed. Empty = failed call, stream, or older row.
        // T-2892: an agent key from another subscription is flagged on any row, billed or not.
        render: (value, record) => {
          const keyMismatch = (record.billingNote || "").startsWith("AgentKeyMismatch:");
          if (!value && !keyMismatch) return "—";
          return (
            <span title={record.billingNote || ""}>
              {value && (
                <Badge variant={value === "Billed" || value === "NotMetered" ? "default" : "danger"}>{value}</Badge>
              )}
              {keyMismatch && (
                <Badge variant="warning" style={{ marginLeft: value ? 4 : 0 }}>Key mismatch</Badge>
              )}
            </span>
          );
        },
      },
      {
        title: "Actions",
        key: "actions",
        width: 130,
        render: (_, record) => (
          <Popconfirm
            title="Delete this log?"
            okText="Delete"
            okButtonProps={{ danger: true, loading: deletingId === record.id }}
            onConfirm={(event) => {
              event?.stopPropagation?.();
              handleDelete(record.id);
            }}
            onCancel={(event) => event?.stopPropagation?.()}
          >
            <Button
              variant="danger"
              size="sm"
              loading={deletingId === record.id}
              onClick={(event) => event.stopPropagation()}
              icon={<DeleteOutlined />}
            >
              Delete
            </Button>
          </Popconfirm>
        ),
      },
    ],
    [deletingId]
  );

  const tableLoading = Boolean(loading?.fetchLogs);
  const clearing = Boolean(loading?.clearLogs);

  return (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <PageHeader
        title="System Logs"
        subtitle="Request activity across the platform, filterable by client and period."
        className="page-header--flush"
      />
      <LogsFilters
        search={search}
        onSearchChange={setSearch}
        range={range}
        onRangeChange={setRange}
        onApply={handleApplyFilters}
        onReset={handleResetFilters}
        pageSize={pagination.pageSize}
        onPageSizeChange={handlePageSizeChange}
        loading={tableLoading}
      />

      <Card>
        <Space style={{ marginBottom: 16 }} wrap>
          <Button
            variant="secondary"
            icon={<ReloadOutlined />}
            onClick={handleRefresh}
            loading={tableLoading}
          >
            Refresh
          </Button>
          <Popconfirm
            title="Are you sure you want to delete all logs?"
            okText="Delete"
            okButtonProps={{ danger: true, loading: clearing }}
            onConfirm={handleClearAll}
          >
            <Button variant="danger" loading={clearing} icon={<DeleteOutlined />}>
              Clear all
            </Button>
          </Popconfirm>
        </Space>

        <Table
          rowKey="id"
          dataSource={logs}
          columns={columns}
          loading={tableLoading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
          }}
          onChange={handleTableChange}
          onRow={(record) => ({
            onClick: () => setSelectedLog(record),
            style: { cursor: "pointer" },
          })}
          scroll={{ x: true }}
        />
      </Card>

      <LogDetailsModal
        log={selectedLog}
        open={Boolean(selectedLog)}
        onClose={() => setSelectedLog(null)}
      />
    </Space>
  );
};

export default LogsPage;
